import {
  ColleaguesType,
  GetColleaguesParamsType,
  GetColleaguesTargetType,
  PaystackBank,
  SliceStatus,
  Wellbit,
  WellbitHistory,
  WellbitTypeTotal,
} from 'interfaces';

import {ActionReducerMapBuilder, createSlice} from '@reduxjs/toolkit';

import {
  sendGreetings,
  sendGreetingsFailure,
  sendGreetingsSuccess,
} from './Collaboration/CollaborationActions';
import {
  getColleagues,
  getColleaguesFailure,
  getColleaguesSuccess,
  resetSearchColleagues,
  searchColleagues,
  searchColleaguesFailure,
  searchColleaguesSuccess,
} from './Collaboration/Colleagues/colleaguesActions';
import {
  clearWellbitsState,
  getProviderColleagueWellbit,
  getProviderColleagueWellbitFailure,
  getProviderColleagueWellbitSuccess,
  getWellbitsData,
  getWellbitsDataFailure,
  getWellbitsDataSuccess,
  getWellbitWithdrawalHistory,
  getWellbitWithdrawalHistoryFailure,
  getWellbitWithdrawalHistorySuccess,
  resetTransactionHistory,
  withdrawWellbits,
  withdrawWellbitsFailure,
  withdrawWellbitsSuccess,
} from './Collaboration/Progress/progressActions';
import {
  sendDigitalPracticeLinkToContacts,
  sendDigitalPracticeLinkToContactsFailure,
  sendDigitalPracticeLinkToContactsSuccess,
} from './DigitalPracticeLinkModal/digitalPracticeLinkActions';
import {
  completeStripeOnboarding,
  completeStripeOnboardingFailure,
  completeStripeOnboardingSuccess,
  createPaystackSubaccount,
  createPaystackSubaccountFailure,
  createPaystackSubaccountSuccess,
  createStripeConnectAccount,
  createStripeConnectAccountFailure,
  createStripeConnectAccountSuccess,
  getPayPalOnboardingReferralLink,
  getPayPalOnboardingReferralLinkFailure,
  getPayPalOnboardingReferralLinkSuccess,
  getPayPalSellerOnboardingDetails,
  getPayPalSellerOnboardingDetailsFailure,
  getPayPalSellerOnboardingDetailsSuccess,
  getSouthAfricanBankList,
  getSouthAfricanBankListFailure,
  getSouthAfricanBankListSuccess,
  reauthenticateStripeOnboarding,
  reauthenticateStripeOnboardingFailure,
  reauthenticateStripeOnboardingSuccess,
  updatePayPalSellerInfo,
  updatePayPalSellerInfoFailure,
  updatePayPalSellerInfoSuccess,
  updatePaystackSubaccount,
  updatePaystackSubaccountFailure,
  updatePaystackSubaccountSuccess,
  verifyPaystackTransaction,
  verifyPaystackTransactionFailure,
  verifyPaystackTransactionSuccess,
} from './Revenue/revenueActions';

interface ColleaguesTypeTemplate {
  data: ColleaguesType[];
  offset?: string;
  hasMore: boolean;
  status: SliceStatus;
  error?: string;
  searchParams?: GetColleaguesParamsType;
}

const ColleaguesTypeTemplateDefaultValue = {
  data: [] as ColleaguesType[],
  offset: '',
  hasMore: false,
  status: SliceStatus.idle,
};

export type DigitalPracticeSliceState = {
  stripeConnect: {
    status: SliceStatus;
  };
  southAfricanBankList: {
    data: PaystackBank[];
    status: SliceStatus;
  };
  wellbits: {
    currentAvailable: number;
    wellbitTypesTotalCount: WellbitTypeTotal;
    totalWellbits: number;
    status: SliceStatus;
  };
  collaboration: {
    cashingHistory: {
      data: WellbitHistory[];
      hasMore: boolean;
      pageNo: number;
      balanceHistory: number;
    };
    status: SliceStatus;
  };
  referrals: ColleaguesTypeTemplate;
  supervisees: ColleaguesTypeTemplate;
  supervisors: ColleaguesTypeTemplate;
  snapshotColleagues: {
    referrals: ColleaguesTypeTemplate;
    supervisees: ColleaguesTypeTemplate;
    supervisors: ColleaguesTypeTemplate;
  };
  providerWellbits: {
    wellbits: Wellbit[];
    hasMore: boolean;
    pageNo: number;
    total: number;
  };
  wellbit: {
    status: SliceStatus;
  };
  status: SliceStatus;
  searchStatus: SliceStatus;
  error: string;
};

export const digitalPracticeInitialState: DigitalPracticeSliceState = {
  stripeConnect: {
    status: SliceStatus.idle,
  },
  southAfricanBankList: {
    data: [],
    status: SliceStatus.idle,
  },
  wellbits: {
    currentAvailable: 0,
    wellbitTypesTotalCount: {} as WellbitTypeTotal,
    totalWellbits: 0,
    status: SliceStatus.idle,
  },
  collaboration: {
    cashingHistory: {
      data: [] as WellbitHistory[],
      hasMore: false,
      pageNo: 1,
      balanceHistory: 0,
    },
    status: SliceStatus.idle,
  },

  referrals: ColleaguesTypeTemplateDefaultValue,
  supervisees: ColleaguesTypeTemplateDefaultValue,
  supervisors: ColleaguesTypeTemplateDefaultValue,
  snapshotColleagues: {
    referrals: ColleaguesTypeTemplateDefaultValue,
    supervisees: ColleaguesTypeTemplateDefaultValue,
    supervisors: ColleaguesTypeTemplateDefaultValue,
  },

  providerWellbits: {
    wellbits: [] as Wellbit[],
    hasMore: false,
    pageNo: 1,
    total: 0,
  },
  wellbit: {
    status: SliceStatus.idle,
  },
  status: SliceStatus.idle,
  searchStatus: SliceStatus.idle,
  error: '',
};

const digitalPracticeSlice = createSlice({
  name: 'digitalPractice',
  initialState: digitalPracticeInitialState,
  reducers: {},
  extraReducers: (
    builder: ActionReducerMapBuilder<DigitalPracticeSliceState>,
  ) =>
    builder
      .addCase(createStripeConnectAccount, state => {
        state.stripeConnect.status = SliceStatus.pending;
      })
      .addCase(createStripeConnectAccountSuccess, state => {
        state.stripeConnect.status = SliceStatus.resolved;
      })
      .addCase(createStripeConnectAccountFailure, state => {
        state.stripeConnect.status = SliceStatus.rejected;
      })
      .addCase(getSouthAfricanBankList, state => {
        state.southAfricanBankList.status = SliceStatus.pending;
      })
      .addCase(getSouthAfricanBankListSuccess, (state, action) => {
        state.southAfricanBankList.data = action.payload;
        state.southAfricanBankList.status = SliceStatus.resolved;
      })
      .addCase(getSouthAfricanBankListFailure, state => {
        state.southAfricanBankList.status = SliceStatus.rejected;
      })
      .addCase(getWellbitsData, state => {
        state.wellbits.status = SliceStatus.pending;
      })
      .addCase(getWellbitsDataSuccess, (state, {payload}) => {
        const {wellbitsTypeCount, currentAvailableWellbits, totalWellbits} =
          payload;
        // Update wellbitTypesTotalCount using direct property assignment
        if (wellbitsTypeCount) {
          Object.keys(wellbitsTypeCount).forEach(key => {
            state.wellbits.wellbitTypesTotalCount[key] = wellbitsTypeCount[key];
          });
        }

        state.wellbits.currentAvailable = currentAvailableWellbits;
        state.wellbits.totalWellbits = totalWellbits;
        state.wellbits.status = SliceStatus.resolved;
      })
      .addCase(getWellbitsDataFailure, state => {
        state.wellbits.status = SliceStatus.rejected;
      })
      .addCase(
        withdrawWellbitsSuccess,
        (state, {payload: {withdrawnAmount}}) => {
          const newAvailable = Math.max(
            0,
            state.wellbits.currentAvailable - withdrawnAmount,
          );
          // Update only currentAvailableWellbits and status
          state.wellbits.currentAvailable = newAvailable;
          state.wellbits.status = SliceStatus.resolved;
        },
      )
      .addCase(withdrawWellbitsFailure, state => {
        state.wellbits.status = SliceStatus.rejected;
      })
      .addCase(getWellbitWithdrawalHistory, state => {
        state.collaboration.status = SliceStatus.pending;
      })
      .addCase(getWellbitWithdrawalHistoryFailure, state => {
        state.collaboration.status = SliceStatus.rejected;
      })
      .addCase(getWellbitWithdrawalHistorySuccess, (state, {payload}) => {
        const {cashingHistory, nextPage, hasMore, balanceHistory} = payload;
        state.collaboration.cashingHistory.data.push(...cashingHistory);
        state.collaboration.cashingHistory.pageNo = nextPage;
        state.collaboration.cashingHistory.hasMore = hasMore;
        state.collaboration.cashingHistory.balanceHistory = balanceHistory;
        state.collaboration.status = SliceStatus.resolved;
      })
      .addCase(resetTransactionHistory, state => {
        state.collaboration.cashingHistory = {
          data: [],
          pageNo: 1,
          hasMore: false,
          balanceHistory: state.collaboration.cashingHistory.balanceHistory,
        };
      })
      .addCase(getColleagues, (state, action) => {
        const target: GetColleaguesTargetType = action.payload.target;
        state[target].status = SliceStatus.pending;
        state[target].error = '';
      })
      .addCase(getColleaguesFailure, (state, action) => {
        const target = action.payload.target;
        const error = action.payload.error;
        state[target].error =
          error instanceof Error ? error.message : String(error);
        state[target].status = SliceStatus.rejected;
      })
      .addCase(
        getColleaguesSuccess,
        (
          state,
          {
            payload: {
              target,
              data: {result, hasMore, offset},
            },
          },
        ) => {
          // Append new results to existing data using array push
          result.forEach(item => {
            state[target].data.push(item);
          });

          state[target].hasMore = hasMore;
          state[target].offset = offset;
          state[target].status = SliceStatus.resolved;

          // Update snapshot colleagues with the same data
          // Copying the entire structure here to ensure proper structure snapshot
          state.snapshotColleagues[target] = {
            hasMore,
            offset,
            data: [...state[target].data], // Need to create a new array for the snapshot
            status: SliceStatus.resolved,
          };
        },
      )
      .addCase(searchColleagues, (state, {payload: {target}}) => {
        if (Object.keys(state.snapshotColleagues[target]).length === 0) {
          const {data, hasMore, offset, error, searchParams} = state[target];
          // Save snapshot of current state with pending status
          if (!state.snapshotColleagues[target]) {
            state.snapshotColleagues[target] = {
              data: [],
              hasMore: false,
              offset: '',
              error: '',
              searchParams: {
                size: 0,
                target,
              },
              status: SliceStatus.pending,
            };
          }
          // Use direct property assignments for better Immer performance
          const snapshot = state.snapshotColleagues[target];
          snapshot.data = data.slice(); // Use slice() for array copy
          snapshot.offset = offset;
          snapshot.hasMore = hasMore;
          snapshot.error = error;
          snapshot.searchParams = searchParams;
          snapshot.status = SliceStatus.pending;
        } else {
          state.searchStatus = SliceStatus.pending;
        }
      })
      .addCase(searchColleaguesFailure, (state, {payload: {target, error}}) => {
        state[target].error =
          error instanceof Error ? error.message : String(error);
        state[target].status = SliceStatus.rejected;
        state.searchStatus = SliceStatus.rejected;
      })
      .addCase(
        searchColleaguesSuccess,
        (
          state,
          {
            payload: {
              target,
              data: {result, hasMore},
            },
          },
        ) => {
          state[target] = {
            data: result,
            hasMore,
            offset: '',
            status: SliceStatus.resolved,
          };
          state.searchStatus = SliceStatus.resolved;
        },
      )
      .addCase(resetSearchColleagues, (state, {payload: target}) => {
        // Restore from snapshot but keep resolved status
        if (state.snapshotColleagues[target]) {
          // Copy all properties from snapshot
          Object.keys(state.snapshotColleagues[target]).forEach(key => {
            if (key !== 'status') {
              state[target][key] = state.snapshotColleagues[target][key];
            }
          });
          state[target].status = SliceStatus.resolved;
        }
      })
      .addCase(getProviderColleagueWellbit, state => {
        state.wellbit.status = SliceStatus.pending;
      })
      .addCase(getProviderColleagueWellbitSuccess, (state, action) => {
        // Use push() instead of concat() for better Immer performance
        action.payload.result.forEach(item => {
          state.providerWellbits.wellbits.push(item);
        });

        const {hasMore, nextPage, total} = action.payload;
        state.providerWellbits = {
          wellbits: state.providerWellbits.wellbits,
          hasMore,
          pageNo: nextPage,
          total,
        };
        state.wellbit.status = SliceStatus.resolved;
      })
      .addCase(getProviderColleagueWellbitFailure, state => {
        state.wellbit.status = SliceStatus.rejected;
      })
      .addCase(withdrawWellbits, state => {
        state.wellbits.status = SliceStatus.pending;
      })
      .addCase(clearWellbitsState, state => {
        state.providerWellbits = {
          wellbits: [] as Wellbit[],
          hasMore: false,
          pageNo: 1,
          total: 0,
        };
      })
      .addDefaultCase(state => state),
});

export const {
  reducer: digitalPracticeReducer,
  name: digitalPracticeReducerName,
} = digitalPracticeSlice;

export type TDigitalPracticeActions =
  | ReturnType<typeof createStripeConnectAccount>
  | ReturnType<typeof createStripeConnectAccountSuccess>
  | ReturnType<typeof createStripeConnectAccountFailure>
  | ReturnType<typeof reauthenticateStripeOnboarding>
  | ReturnType<typeof reauthenticateStripeOnboardingSuccess>
  | ReturnType<typeof reauthenticateStripeOnboardingFailure>
  | ReturnType<typeof completeStripeOnboarding>
  | ReturnType<typeof completeStripeOnboardingSuccess>
  | ReturnType<typeof completeStripeOnboardingFailure>
  | ReturnType<typeof getWellbitsData>
  | ReturnType<typeof getWellbitsDataSuccess>
  | ReturnType<typeof getWellbitsDataFailure>
  | ReturnType<typeof withdrawWellbits>
  | ReturnType<typeof withdrawWellbitsSuccess>
  | ReturnType<typeof withdrawWellbitsFailure>
  | ReturnType<typeof getWellbitWithdrawalHistory>
  | ReturnType<typeof getWellbitWithdrawalHistorySuccess>
  | ReturnType<typeof getWellbitWithdrawalHistoryFailure>
  | ReturnType<typeof resetTransactionHistory>
  | ReturnType<typeof getColleagues>
  | ReturnType<typeof getColleaguesSuccess>
  | ReturnType<typeof getColleaguesFailure>
  | ReturnType<typeof searchColleagues>
  | ReturnType<typeof searchColleaguesFailure>
  | ReturnType<typeof searchColleaguesSuccess>
  | ReturnType<typeof resetSearchColleagues>
  | ReturnType<typeof sendGreetings>
  | ReturnType<typeof sendGreetingsSuccess>
  | ReturnType<typeof sendGreetingsFailure>
  | ReturnType<typeof sendDigitalPracticeLinkToContacts>
  | ReturnType<typeof sendDigitalPracticeLinkToContactsSuccess>
  | ReturnType<typeof sendDigitalPracticeLinkToContactsFailure>
  | ReturnType<typeof getProviderColleagueWellbit>
  | ReturnType<typeof getProviderColleagueWellbitSuccess>
  | ReturnType<typeof getProviderColleagueWellbitFailure>
  | ReturnType<typeof clearWellbitsState>
  | ReturnType<typeof getPayPalOnboardingReferralLink>
  | ReturnType<typeof getPayPalOnboardingReferralLinkSuccess>
  | ReturnType<typeof getPayPalOnboardingReferralLinkFailure>
  | ReturnType<typeof updatePayPalSellerInfo>
  | ReturnType<typeof updatePayPalSellerInfoSuccess>
  | ReturnType<typeof updatePayPalSellerInfoFailure>
  | ReturnType<typeof getPayPalSellerOnboardingDetails>
  | ReturnType<typeof getPayPalSellerOnboardingDetailsSuccess>
  | ReturnType<typeof getPayPalSellerOnboardingDetailsFailure>
  | ReturnType<typeof createPaystackSubaccount>
  | ReturnType<typeof createPaystackSubaccountSuccess>
  | ReturnType<typeof createPaystackSubaccountFailure>
  | ReturnType<typeof updatePaystackSubaccount>
  | ReturnType<typeof updatePaystackSubaccountSuccess>
  | ReturnType<typeof updatePaystackSubaccountFailure>
  | ReturnType<typeof getSouthAfricanBankList>
  | ReturnType<typeof getSouthAfricanBankListSuccess>
  | ReturnType<typeof getSouthAfricanBankListFailure>;

export const digitalPracticeActions = {
  createStripeConnectAccount,
  createStripeConnectAccountSuccess,
  createStripeConnectAccountFailure,
  reauthenticateStripeOnboarding,
  reauthenticateStripeOnboardingFailure,
  reauthenticateStripeOnboardingSuccess,
  completeStripeOnboarding,
  completeStripeOnboardingFailure,
  completeStripeOnboardingSuccess,
  getWellbitsData,
  getWellbitsDataSuccess,
  getWellbitsDataFailure,
  withdrawWellbits,
  withdrawWellbitsSuccess,
  withdrawWellbitsFailure,
  getWellbitWithdrawalHistory,
  getWellbitWithdrawalHistorySuccess,
  getWellbitWithdrawalHistoryFailure,
  resetTransactionHistory,
  getColleagues,
  getColleaguesFailure,
  getColleaguesSuccess,
  searchColleagues,
  searchColleaguesFailure,
  searchColleaguesSuccess,
  resetSearchColleagues,
  sendGreetingsFailure,
  sendGreetings,
  sendGreetingsSuccess,
  sendDigitalPracticeLinkToContactsFailure,
  sendDigitalPracticeLinkToContacts,
  sendDigitalPracticeLinkToContactsSuccess,
  getProviderColleagueWellbit,
  getProviderColleagueWellbitSuccess,
  getProviderColleagueWellbitFailure,
  clearWellbitsState,
  getPayPalOnboardingReferralLink,
  getPayPalOnboardingReferralLinkSuccess,
  getPayPalOnboardingReferralLinkFailure,
  updatePayPalSellerInfo,
  updatePayPalSellerInfoSuccess,
  updatePayPalSellerInfoFailure,
  getPayPalSellerOnboardingDetails,
  getPayPalSellerOnboardingDetailsSuccess,
  getPayPalSellerOnboardingDetailsFailure,
  createPaystackSubaccount,
  createPaystackSubaccountSuccess,
  createPaystackSubaccountFailure,
  updatePaystackSubaccount,
  updatePaystackSubaccountSuccess,
  updatePaystackSubaccountFailure,
  getSouthAfricanBankList,
  getSouthAfricanBankListSuccess,
  getSouthAfricanBankListFailure,
  verifyPaystackTransaction,
  verifyPaystackTransactionSuccess,
  verifyPaystackTransactionFailure,
};

export type DigitalPracticeState = ReturnType<typeof digitalPracticeReducer>;
